/**
 * Reprise d'un live après être sorti de la page (PUR, testé).
 *
 * `live-join` (et `live-test start`) renvoient les réponses déjà ENREGISTRÉES
 * de l'élève + le corrigé de ces étapes. On les reconvertit en résultats du
 * player : l'élève retrouve ses choix, ne peut pas répondre deux fois, et les
 * popups des questions passées ne se rejouent pas. Seule la question EN COURS,
 * si elle est déjà répondue, garde sa popup à la résolution.
 */

export interface ResumedLiveAnswer {
  step_index: number;
  was_correct: boolean;
  points: number;
  eligible: boolean;
  chosen_block_id: string | null;
  chosen_block_ids: string[];
  correct_block_id: string | null;
  correct_block_ids: string[];
}

export interface ResumedStepResult {
  stepIndex: number;
  wasCorrect: boolean;
  points: number;
  eligible: boolean;
  correctBlockId: string | null;
  correctBlockIds: string[];
  chosenBlockId: string | null;
  chosenBlockIds: string[];
}

export function toStepResult(a: ResumedLiveAnswer): ResumedStepResult {
  const chosen = a.chosen_block_ids?.length
    ? a.chosen_block_ids
    : a.chosen_block_id
      ? [a.chosen_block_id]
      : [];
  return {
    stepIndex: a.step_index,
    wasCorrect: a.was_correct,
    points: a.points,
    eligible: a.eligible,
    correctBlockId: a.correct_block_id,
    correctBlockIds: a.correct_block_ids?.length
      ? a.correct_block_ids
      : a.correct_block_id
        ? [a.correct_block_id]
        : [],
    chosenBlockId: a.chosen_block_id ?? chosen[0] ?? null,
    chosenBlockIds: chosen,
  };
}

export interface ResumePlan {
  /** Résultats à poser (étapes absentes de `known` seulement). */
  results: Record<number, ResumedStepResult>;
  /** Étapes à marquer « déjà soumises » (aucun nouvel envoi). */
  answered: number[];
  /** L'étape en cours était déjà répondue par l'élève (popup à la résolution). */
  currentAnswered: boolean;
}

/** Ce qu'il faut restaurer, sans écraser ce que la page connaît déjà. */
export function planLiveResume(
  answers: readonly ResumedLiveAnswer[] | null | undefined,
  currentStep: number,
  known: ReadonlySet<number>,
): ResumePlan {
  const results: Record<number, ResumedStepResult> = {};
  const answered: number[] = [];
  let currentAnswered = false;
  for (const a of answers ?? []) {
    if (!Number.isInteger(a?.step_index) || a.step_index < 0 || known.has(a.step_index)) continue;
    const r = toStepResult(a);
    results[a.step_index] = r;
    answered.push(a.step_index);
    if (a.step_index === currentStep && r.chosenBlockIds.length > 0) currentAnswered = true;
  }
  return { results, answered, currentAnswered };
}
